import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { User } from '@/types/user.types';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { fetchSites } from '@/features/sites/sitesThunks';

interface PurchaseVerificationModalProps {
  open: boolean;
  onClose: () => void;
  onContinue: (data: User) => void;
  userData: User | null;
}

export interface PurchaseVerificationData {
  name: string;
  email: string;
  idNumber: string;
  phone: string;
  site: string;
}

const PurchaseVerificationModal: React.FC<PurchaseVerificationModalProps> = ({
  open,
  onClose,
  onContinue,
  userData
}) => {
  const dispatch = useAppDispatch();
  const { sites, loading: sitesLoading } = useAppSelector((state) => state.sites);

  const [formData, setFormData] = useState<PurchaseVerificationData>({
    name: userData?.displayName || '',
    email: userData?.email || '',
    idNumber: userData?.identifier || '',
    phone: userData?.phoneNumber || '',
    site: userData?.site || '',
  });

  const [phoneError, setPhoneError] = useState<string>('');

  // Update form data when userData changes
  useEffect(() => {
    if (userData) {
      setFormData({
        name: userData.displayName || '',
        email: userData.email || '',
        idNumber: userData.identifier || '',
        phone: userData.phoneNumber || '',
        site: userData.site || '',
      });
    }
  }, [userData]);

  // Fetch sites on component mount
  useEffect(() => {
    if (sites.length === 0) {
      dispatch(fetchSites());
    }
  }, [dispatch, sites.length]);

  const validateColombianPhone = (phone: string): boolean => {
    // Colombian mobile numbers: 10 digits, starting with 3
    const phoneRegex = /^3\d{9}$/;
    return phoneRegex.test(phone);
  };

  const handleChange = (field: keyof PurchaseVerificationData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;

    if (field === 'phone') {
      // Only allow numbers
      const numericValue = value.replace(/\D/g, '');
      setFormData({
        ...formData,
        [field]: numericValue,
      });

      // Validate phone number
      if (numericValue && !validateColombianPhone(numericValue)) {
        setPhoneError('Número celular inválido. Debe tener 10 dígitos y empezar con 3');
      } else {
        setPhoneError('');
      }
    } else {
      setFormData({
        ...formData,
        [field]: value,
      });
    }
  };

  const handleContinue = () => {
    onContinue({
      displayName: formData.name,
      email: formData.email,
      identifier: formData.idNumber,
      identifierType: 'CC',
      phoneNumber: formData.phone,
      site: formData.site,
      createdAt: userData?.createdAt || '',
    });
    setFormData({ ...formData, phone: "", site: "" });
  };

  const isFormValid = () => {
    return (
      formData.name.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.idNumber.trim() !== '' &&
      formData.phone.trim() !== '' &&
      formData.site.trim() !== '' &&
      !phoneError &&
      validateColombianPhone(formData.phone)
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: { xs: 2, md: 4 },
          width: { xs: '100%', md: '800px' },
        },
      }}
    >
      <DialogContent sx={{ p: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 4 }}>
          <CheckCircleIcon
            sx={{
              fontSize: { xs: 30, md: 40 },
              color: 'primary.main',
            }}
          />
          <Typography
            variant="h5"
            component="h2"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1.25rem', md: '1.5rem' },
              textAlign: 'center',
            }}
          >
            Verifica la Información de Compra
          </Typography>
        </Box>

        {/* Personal Information Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              fontSize: { xs: '0.8rem', md: '1rem' },
              color: 'text.secondary',
              borderBottom: '1px solid #ccc',
              mb: 3,
            }}
          >
            Información Personal
          </Typography>

          <Stack spacing={3}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 2, md: 3 }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    mb: 1,
                    fontSize: { xs: '0.875rem', md: '1rem' },
                  }}
                >
                  Nombre
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Nombre"
                  disabled
                  value={formData.name}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 40,
                      backgroundColor: '#F5F5F5',
                      '& fieldset': {
                        borderColor: 'transparent',

                      },
                      '&.Mui-disabled': {
                        '& input': {
                          WebkitTextFillColor: '#000000',
                        },
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    mb: 1,
                    fontSize: { xs: '0.875rem', md: '1rem' },
                  }}
                >
                  Correo
                </Typography>
                <TextField
                  fullWidth
                  disabled
                  placeholder="email"
                  value={formData.email}
                  type="email"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 40,
                      backgroundColor: '#F5F5F5',
                      '& fieldset': {
                        borderColor: 'transparent',
                      },
                      '&.Mui-disabled': {
                        '& input': {
                          WebkitTextFillColor: '#000000',
                        },
                      },
                    },
                  }}
                />
              </Box>
            </Stack>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 2, md: 3 }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    mb: 1,
                    fontSize: { xs: '0.875rem', md: '1rem' },
                  }}
                >
                  Cédula de Ciudadanía
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Identificación"
                  value={formData.idNumber}
                  disabled
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 40,
                      backgroundColor: '#F5F5F5',
                      '& fieldset': {
                        borderColor: 'transparent',
                      },
                      '&.Mui-disabled': {
                        '& input': {
                          WebkitTextFillColor: '#000000',
                        },
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    mb: 1,
                    fontSize: { xs: '0.875rem', md: '1rem' },
                  }}
                >
                  Celular *
                </Typography>
                <TextField
                  fullWidth
                  value={formData.phone}
                  onChange={handleChange('phone')}
                  error={!!phoneError}
                  helperText={phoneError}
                  slotProps={{
                    htmlInput: {
                      maxLength: 10,
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 40
                    },
                  }}
                />
              </Box>
            </Stack>
          </Stack>
        </Box>

        {/* Institutional Information Section */}
        <Box sx={{ mb: 4, mt: 4 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              fontSize: { xs: '0.8rem', md: '1rem' },
              color: 'text.secondary',
              borderBottom: '1px solid #ccc',
              mb: 3,
            }}
          >
            Información Institucional
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center', mt: 4 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                mb: 1,
                fontSize: { xs: '0.875rem', md: '1rem' },
              }}
            >
              Sede
            </Typography>
            <TextField
              fullWidth
              select
              placeholder="Sede Principal"
              value={formData.site}
              onChange={handleChange('site')}
              disabled={sitesLoading || sites.length === 0}
              sx={{
                width: { xs: '100%', md: '40%' },
                '& .MuiOutlinedInput-root': {
                  height: 40,
                  backgroundColor: 'white',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#c0c0c0',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            >
              {sitesLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                </MenuItem>
              ) : (
                sites.map((item, index) => (
                  <MenuItem key={item.id || index} value={item.Name}>
                    {item.Name}
                  </MenuItem>
                ))
              )}
            </TextField>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                width: { xs: '100%', md: '60%' },
              }}
            >
              <Button
                variant="contained"
                onClick={handleContinue}
                disabled={!isFormValid()}
                sx={{
                  backgroundColor: 'primary.dark',
                  color: 'white',
                  fontWeight: 600,
                  textTransform: 'none',
                  px: { xs: 4, md: 6 },
                  py: { xs: 1.2, md: 1.5 },
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  minWidth: { xs: '100%', md: 200 },
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#a0a0a0',
                  },
                }}
              >
                Continuar
              </Button>
            </Box>
          </Box>

        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseVerificationModal;
